import { readonly, ref } from 'vue'
import darkThemeCss from '../styles/app-dark-theme.css?inline'

export type ColorTheme = 'light' | 'dark'

const STORAGE_KEY = 'openrare-color-theme'
const DARK_STYLE_ID = 'openrare-dark-theme'
const colorTheme = ref<ColorTheme>('dark')

let darkStyleElement: HTMLStyleElement | null = null

function readSavedTheme(): ColorTheme | null {
  try {
    const savedTheme = window.localStorage.getItem(STORAGE_KEY)
    return savedTheme === 'light' || savedTheme === 'dark' ? savedTheme : null
  } catch {
    return null
  }
}

function updateThemeColor(theme: ColorTheme): void {
  const themeColor = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
  themeColor?.setAttribute('content', theme === 'dark' ? '#040509' : '#ffffff')
}

export function setColorTheme(theme: ColorTheme, persist = true): void {
  colorTheme.value = theme

  if (darkStyleElement) {
    darkStyleElement.media = theme === 'dark' ? 'all' : 'not all'
  }

  document.documentElement.dataset.colorTheme = theme
  document.documentElement.style.colorScheme = theme
  updateThemeColor(theme)

  if (!persist) return
  try {
    window.localStorage.setItem(STORAGE_KEY, theme)
  } catch {
    // Storage can be unavailable in privacy-focused embedded browsers.
  }
}

export function toggleColorTheme(): void {
  setColorTheme(colorTheme.value === 'dark' ? 'light' : 'dark')
}

export function installColorTheme() {
  darkStyleElement = document.getElementById(DARK_STYLE_ID) as HTMLStyleElement | null

  if (!darkStyleElement) {
    darkStyleElement = document.createElement('style')
    darkStyleElement.id = DARK_STYLE_ID
    darkStyleElement.textContent = darkThemeCss
    document.head.appendChild(darkStyleElement)
  }

  setColorTheme(readSavedTheme() ?? 'dark', false)

  window.OpenRareTheme = {
    getTheme: () => colorTheme.value,
    setTheme: setColorTheme,
    toggleTheme: toggleColorTheme,
  }

  return useColorTheme()
}

export function useColorTheme() {
  return {
    theme: readonly(colorTheme),
    setTheme: setColorTheme,
    toggleTheme: toggleColorTheme,
  }
}

declare global {
  interface Window {
    OpenRareTheme: {
      getTheme: () => ColorTheme
      setTheme: (theme: ColorTheme) => void
      toggleTheme: () => void
    }
  }
}
